import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../utils/api';
import { formatDate } from '../../utils/dateUtils';
import { usePermissions } from '../../hooks/usePermissions';
import { NavigationSidebar } from '../../components/layout/NavigationSidebar';
import { PageContainer } from '../../components/layout/PageContainer';
import { useToast } from '../../components/ui/Toast';
import { PhoneInput, isValidPhoneInputValue } from '../../components/ui/PhoneInput';

export const Route = createFileRoute('/user-management/')({
  component: UserManagementPage,
});

interface UserItem {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  permission: string;
  isActive: boolean;
  emailVerified: boolean;
  phoneNumber: string | null;
  phoneNumberVerified: boolean;
  mustChangePassword: boolean;
  /** Per-row company context — populated for super_admin's all-users view, or the active company for company_admin. */
  companyId: string | null;
  companyName: string | null;
  lastLoginAt: string | null;
  createdAt: string;
}

interface InviteItem {
  id: string;
  email: string;
  role: string;
  permission: string;
  accepted: boolean;
  expiresAt: string;
  createdAt: string;
  invitedBy: string;
}

const roleLabels: Record<string, string> = {
  super_admin: 'Super Admin',
  company_admin: 'Company Admin',
  company_user: 'Team Member',
};

function UserManagementPage() {
  const { canManageUsers, isSuperAdmin } = usePermissions();
  const { showToast, ToastContainer } = useToast();
  const [users, setUsers] = useState<UserItem[]>([]);
  const [invites, setInvites] = useState<InviteItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Per-row "More actions" dropdown state — only one menu open at a time.
  const [openMenuUserId, setOpenMenuUserId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Confirmation dialog for destructive primitives (reset 2fa, force change pw, unlock).
  // window.confirm is kept for delete/cancel-invite/deactivate which already used it.
  const [confirm, setConfirm] = useState<{
    title: string;
    message: string;
    confirmLabel: string;
    confirmTone: 'red' | 'amber' | 'purple';
    onConfirm: () => Promise<void> | void;
  } | null>(null);
  const [confirmRunning, setConfirmRunning] = useState(false);

  // Phone-edit modal state
  const [phoneEdit, setPhoneEdit] = useState<{ user: UserItem; value: string } | null>(null);
  const [phoneEditSaving, setPhoneEditSaving] = useState(false);

  // Invite modal state
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'company_user', permission: 'view' });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');

  // Close the per-row menu when clicking outside it.
  useEffect(() => {
    if (!openMenuUserId) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuUserId(null);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [openMenuUserId]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersData, invitesData] = await Promise.all([
        api.get('/users') as Promise<UserItem[]>,
        api.get('/users/invites') as Promise<InviteItem[]>,
      ]);
      setUsers(usersData);
      setInvites(invitesData);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canManageUsers) loadData();
  }, [canManageUsers, loadData]);

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    setInviteError('');
    setInviteSuccess('');
    setInviteLoading(true);
    try {
      await api.post('/users/invite', inviteForm);
      setInviteSuccess(`Invite sent to ${inviteForm.email}`);
      setInviteForm({ email: '', role: 'company_user', permission: 'view' });
      loadData();
      setTimeout(() => {
        setShowInvite(false);
        setInviteSuccess('');
      }, 2000);
    } catch (err) {
      setInviteError((err as Error).message);
    } finally {
      setInviteLoading(false);
    }
  }

  async function handleRoleChange(userId: string, role: string, permission: string) {
    try {
      await api.put(`/users/${userId}/role`, { role, permission });
      loadData();
    } catch (err) {
      showToast((err as Error).message, 'error');
    }
  }

  async function handleCancelInvite(inviteId: string, email: string) {
    const confirmed = window.confirm(`Cancel the invitation to ${email}?`);
    if (!confirmed) return;
    try {
      await api.del(`/users/invites/${inviteId}`);
      showToast(`Invitation to ${email} cancelled`, 'success');
      loadData();
    } catch (err) {
      showToast((err as Error).message, 'error');
    }
  }

  async function handleToggleActive(userId: string, isActive: boolean) {
    try {
      if (isActive) {
        await api.put(`/users/${userId}/deactivate`, {});
        showToast('User deactivated', 'success');
      } else {
        await api.put(`/users/${userId}/reactivate`, {});
        showToast('User reactivated', 'success');
      }
      loadData();
    } catch (err) {
      showToast((err as Error).message, 'error');
    }
  }

  async function handleDeleteUser(userId: string, name: string) {
    const confirmed = window.confirm(
      `Are you sure you want to permanently delete ${name}? This action cannot be undone.`
    );
    if (!confirmed) return;
    try {
      await api.del(`/users/${userId}`);
      showToast(`${name} deleted`, 'success');
      loadData();
    } catch (err) {
      showToast((err as Error).message, 'error');
    }
  }

  // ─── Auth admin primitives (PR 1 endpoints) ───
  function fullName(u: UserItem) {
    return `${u.firstName} ${u.lastName}`.trim() || u.email;
  }

  async function postPrimitive(
    path: string,
    successMessage: string,
    failurePrefix: string,
  ): Promise<boolean> {
    try {
      await api.post(path, {});
      showToast(successMessage, 'success');
      loadData();
      return true;
    } catch (err) {
      showToast(`${failurePrefix}: ${(err as Error).message}`, 'error');
      return false;
    }
  }

  function handleSendReset(u: UserItem) {
    setOpenMenuUserId(null);
    postPrimitive(
      `/users/${u.id}/send-reset`,
      `Password reset email sent to ${u.email}`,
      'Failed to send reset email',
    );
  }

  function handleUnlock(u: UserItem) {
    setOpenMenuUserId(null);
    postPrimitive(
      `/users/${u.id}/unlock`,
      `${fullName(u)} unlocked`,
      'Failed to unlock account',
    );
  }

  function handleForceChangePassword(u: UserItem) {
    setOpenMenuUserId(null);
    setConfirm({
      title: 'Force change password',
      message: `${fullName(u)} will be required to set a new password the next time they sign in. They will not be locked out — they can self-serve via the forgot-password link if needed.`,
      confirmLabel: 'Flag for password change',
      confirmTone: 'amber',
      onConfirm: async () => {
        await postPrimitive(
          `/users/${u.id}/force-change-password`,
          `${fullName(u)} flagged to change password`,
          'Failed to flag user',
        );
      },
    });
  }

  function handleReset2fa(u: UserItem) {
    setOpenMenuUserId(null);
    setConfirm({
      title: 'Reset two-factor authentication',
      message: `This clears ${fullName(u)}'s phone-verified flag, any active sign-in codes, and signs them out of every device. They will need to re-enrol their second factor on next sign-in. Use this when a user has lost their phone or authenticator.`,
      confirmLabel: 'Reset 2FA',
      confirmTone: 'red',
      onConfirm: async () => {
        await postPrimitive(
          `/users/${u.id}/reset-2fa`,
          `2FA reset for ${fullName(u)}`,
          'Failed to reset 2FA',
        );
      },
    });
  }

  async function runConfirm() {
    if (!confirm) return;
    setConfirmRunning(true);
    try {
      await confirm.onConfirm();
    } finally {
      setConfirmRunning(false);
      setConfirm(null);
    }
  }

  function handleSetPhone(u: UserItem) {
    setOpenMenuUserId(null);
    setPhoneEdit({ user: u, value: u.phoneNumber ?? '' });
  }

  async function submitPhoneEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!phoneEdit) return;
    setPhoneEditSaving(true);
    try {
      await api.put(`/users/${phoneEdit.user.id}/phone`, { phoneNumber: phoneEdit.value });
      const trimmed = phoneEdit.value.trim();
      showToast(
        trimmed.length === 0
          ? `Phone number cleared for ${fullName(phoneEdit.user)}`
          : `Phone number saved for ${fullName(phoneEdit.user)}`,
        'success'
      );
      setPhoneEdit(null);
      loadData();
    } catch (err) {
      showToast(`Failed to save phone: ${(err as Error).message}`, 'error');
    } finally {
      setPhoneEditSaving(false);
    }
  }

  if (!canManageUsers) {
    return (
      <div className="flex flex-1 overflow-auto">
        <NavigationSidebar />
        <PageContainer>
          <div className="p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">User Management</h1>
            <p className="text-gray-500">You don't have permission to manage users.</p>
          </div>
        </PageContainer>
      </div>
    );
  }

  return (
    <div className="flex flex-1 overflow-auto">
      <NavigationSidebar />
      <PageContainer>
        <div className="p-6 space-y-6">
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          {/* Header with invite button */}
          <div className="flex items-center justify-between">
            <p className="text-gray-500">Manage team members and invitations</p>
            <button
              onClick={() => setShowInvite(true)}
              className="px-4 py-2 text-white text-sm font-medium rounded-lg"
              style={{ background: 'var(--color-accent)' }}
            >
              + Invite User
            </button>
          </div>

          {error && (
            <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
          )}

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-8 h-8 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Users table */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {isSuperAdmin ? 'All users' : 'Team members'} ({users.length})
                  </h2>
                  {isSuperAdmin && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      Showing every user across every company. Acting on a row affects that user's primary company; the audit entry lands there.
                    </p>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        {isSuperAdmin && (
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                        )}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Permission</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {users.map((u) => (
                        <tr key={u.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            {u.firstName} {u.lastName}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">{u.email}</td>
                          {isSuperAdmin && (
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {u.companyName ?? <span className="text-gray-400 italic">—</span>}
                            </td>
                          )}
                          <td className="px-6 py-4">
                            {u.role === 'super_admin' ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                Super Admin
                              </span>
                            ) : (
                              <select
                                value={u.role}
                                onChange={(e) => handleRoleChange(u.id, e.target.value, u.permission)}
                                className="text-sm border border-gray-300 rounded px-2 py-1"
                              >
                                <option value="company_admin">Company Admin</option>
                                <option value="company_user">Team Member</option>
                              </select>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {u.role === 'super_admin' ? (
                              <span className="text-xs text-gray-400 italic">platform-wide</span>
                            ) : (
                              <select
                                value={u.permission}
                                onChange={(e) => handleRoleChange(u.id, u.role, e.target.value)}
                                className="text-sm border border-gray-300 rounded px-2 py-1"
                              >
                                <option value="view">View</option>
                                <option value="edit">Edit</option>
                              </select>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            {u.phoneNumber ? (
                              <span className="font-mono text-gray-700">{u.phoneNumber}</span>
                            ) : (
                              <span className="text-gray-400 italic">Not set</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              u.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {u.isActive ? 'Active' : 'Deactivated'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            {/*
                              The dropdown shows on every row — server-side
                              guards in resolveTargetUser are the actual
                              source of truth for what's allowed. Hiding the
                              dropdown on a row would also hide the harmless
                              "Set phone number" self-edit, which is what
                              caused super-admin users to think the menu was
                              broken.
                            */}
                            <div className="relative inline-block text-left">
                              <button
                                onClick={() => setOpenMenuUserId(openMenuUserId === u.id ? null : u.id)}
                                className="inline-flex items-center justify-center w-8 h-8 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                                aria-label={`Actions for ${fullName(u)}`}
                                aria-haspopup="menu"
                                aria-expanded={openMenuUserId === u.id}
                              >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM18 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                              </button>

                              {openMenuUserId === u.id && (
                                <div
                                  ref={menuRef}
                                  role="menu"
                                  className="absolute right-0 mt-2 w-60 bg-white rounded-lg shadow-lg ring-1 ring-black/5 z-20 py-1"
                                >
                                  <div className="px-3 py-2 text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
                                    Auth admin
                                  </div>
                                  <button
                                    role="menuitem"
                                    onClick={() => handleSendReset(u)}
                                    className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                  >
                                    Send password reset email
                                  </button>
                                  <button
                                    role="menuitem"
                                    onClick={() => handleForceChangePassword(u)}
                                    className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                  >
                                    Force change password
                                  </button>
                                  <button
                                    role="menuitem"
                                    onClick={() => handleReset2fa(u)}
                                    className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                  >
                                    Reset two-factor
                                  </button>
                                  <button
                                    role="menuitem"
                                    onClick={() => handleUnlock(u)}
                                    className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                  >
                                    Unlock account
                                  </button>
                                  <button
                                    role="menuitem"
                                    onClick={() => handleSetPhone(u)}
                                    className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                  >
                                    Set phone number
                                  </button>

                                  <div className="px-3 py-2 mt-1 text-xs uppercase tracking-wide text-gray-400 border-t border-gray-100">
                                    Account
                                  </div>
                                  <button
                                    role="menuitem"
                                    onClick={() => { setOpenMenuUserId(null); handleToggleActive(u.id, u.isActive); }}
                                    className={`block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                                      u.isActive ? 'text-amber-700' : 'text-green-700'
                                    }`}
                                  >
                                    {u.isActive ? 'Deactivate user' : 'Reactivate user'}
                                  </button>
                                  <button
                                    role="menuitem"
                                    onClick={() => { setOpenMenuUserId(null); handleDeleteUser(u.id, fullName(u)); }}
                                    className="block w-full text-left px-3 py-2 text-sm text-red-700 hover:bg-red-50"
                                  >
                                    Delete user permanently
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pending invites */}
              {invites.filter((i) => !i.accepted).length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">
                      Pending Invitations ({invites.filter((i) => !i.accepted).length})
                    </h2>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Permission</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invited By</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {invites
                          .filter((i) => !i.accepted)
                          .map((inv) => (
                            <tr key={inv.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 text-sm text-gray-900">{inv.email}</td>
                              <td className="px-6 py-4 text-sm text-gray-500">{roleLabels[inv.role] || inv.role}</td>
                              <td className="px-6 py-4 text-sm text-gray-500 capitalize">{inv.permission}</td>
                              <td className="px-6 py-4 text-sm text-gray-500">{inv.invitedBy}</td>
                              <td className="px-6 py-4 text-sm text-gray-500">
                                {formatDate(inv.expiresAt)}
                              </td>
                              <td className="px-6 py-4">
                                <button
                                  onClick={() => handleCancelInvite(inv.id, inv.email)}
                                  className="text-xs font-medium px-3 py-1 rounded text-red-600 hover:bg-red-50"
                                >
                                  Cancel
                                </button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Invite modal */}
          {showInvite && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Invite Team Member</h2>
                <form onSubmit={handleInvite} className="space-y-4">
                  {inviteError && (
                    <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">{inviteError}</div>
                  )}
                  {inviteSuccess && (
                    <div className="bg-green-50 text-green-700 px-4 py-3 rounded-lg text-sm">{inviteSuccess}</div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                    <input
                      type="email"
                      required
                      value={inviteForm.email}
                      onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                      placeholder="colleague@company.com"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                      <select
                        value={inviteForm.role}
                        onChange={(e) => setInviteForm((f) => ({ ...f, role: e.target.value }))}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg"
                      >
                        <option value="company_user">Team Member</option>
                        <option value="company_admin">Company Admin</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Permission</label>
                      <select
                        value={inviteForm.permission}
                        onChange={(e) => setInviteForm((f) => ({ ...f, permission: e.target.value }))}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg"
                      >
                        <option value="view">View Only</option>
                        <option value="edit">Can Edit</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => { setShowInvite(false); setInviteError(''); setInviteSuccess(''); }}
                      className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={inviteLoading}
                      className="px-4 py-2 text-sm text-white font-medium rounded-lg disabled:opacity-50"
                      style={{ background: 'var(--color-accent)' }}
                    >
                      {inviteLoading ? 'Sending...' : 'Send Invite'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Phone-edit modal */}
          {phoneEdit && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <form onSubmit={submitPhoneEdit} className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">
                  Set phone number
                </h2>
                <p className="text-sm text-gray-500 mb-4">
                  for <span className="font-medium text-gray-700">{fullName(phoneEdit.user)}</span>
                </p>
                <label htmlFor="admin-phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile phone (international format)
                </label>
                <PhoneInput
                  id="admin-phone"
                  value={phoneEdit.value}
                  onChange={(v) => setPhoneEdit({ ...phoneEdit, value: v })}
                  placeholder="7700 900123"
                  invalid={!isValidPhoneInputValue(phoneEdit.value)}
                />
                {phoneEdit.value.length > 0 && !isValidPhoneInputValue(phoneEdit.value) && (
                  <p className="text-xs text-red-600 mt-2">
                    Enter a valid international phone number.
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  Saving resets the verified flag — the user must receive at least one sign-in code at this number for it to count as verified. Leave blank to clear.
                </p>
                <div className="flex justify-end gap-3 pt-5">
                  <button
                    type="button"
                    onClick={() => setPhoneEdit(null)}
                    disabled={phoneEditSaving}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={phoneEditSaving || !isValidPhoneInputValue(phoneEdit.value)}
                    className="px-4 py-2 text-sm text-white font-medium rounded-lg disabled:opacity-50"
                    style={{ background: 'var(--color-accent)' }}
                  >
                    {phoneEditSaving ? 'Saving…' : 'Save phone number'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Confirmation modal for destructive auth-admin primitives */}
          {confirm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">{confirm.title}</h2>
                <p className="text-sm text-gray-600 mb-6">{confirm.message}</p>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setConfirm(null)}
                    disabled={confirmRunning}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={runConfirm}
                    disabled={confirmRunning}
                    className={`px-4 py-2 text-sm text-white font-medium rounded-lg disabled:opacity-50 ${
                      confirm.confirmTone === 'red'
                        ? 'bg-red-600 hover:bg-red-700'
                        : confirm.confirmTone === 'amber'
                          ? 'bg-amber-600 hover:bg-amber-700'
                          : 'bg-purple-700 hover:bg-purple-800'
                    }`}
                  >
                    {confirmRunning ? 'Working…' : confirm.confirmLabel}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        <ToastContainer />
      </PageContainer>
    </div>
  );
}
